import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, X, Save, Trash2, ArrowUp, ArrowDown, ChevronRight, Layers } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { v: "text", l: "Yazı" },
  { v: "number", l: "Sayı" },
  { v: "select", l: "Seçmeli (tekli)" },
  { v: "multiselect", l: "Çoklu Seçmeli" },
  { v: "boolean", l: "Evet / Hayır" },
  { v: "object", l: "Obje (alt-parametreli liste)" },
];

const newField = () => ({ key: crypto.randomUUID(), label: "", type: "text", options: [], default: null, fields: [] });

const arrUpdate = (list, i, item) => list.map((x, idx) => (idx === i ? item : x));
const arrRemove = (list, i) => list.filter((_, idx) => idx !== i);
const arrMove = (list, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const next = list.slice();
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

function OptionsEditor({ field, onChange }) {
  const [input, setInput] = useState("");
  const options = field.options || [];
  const add = () => {
    const v = input.trim();
    if (!v || options.includes(v)) { setInput(""); return; }
    onChange({ ...field, options: [...options, v] });
    setInput("");
  };
  const remove = (o) => {
    const nextOpts = options.filter((x) => x !== o);
    let def = field.default;
    if (field.type === "select" && def === o) def = null;
    if (field.type === "multiselect" && Array.isArray(def)) def = def.filter((x) => x !== o);
    onChange({ ...field, options: nextOpts, default: def });
  };
  return (
    <div className="mt-2">
      <Label className="text-xs text-zinc-500">Seçenekler</Label>
      <div className="flex flex-wrap gap-1.5 mt-1 mb-2">
        {options.length === 0 && <span className="text-xs text-zinc-400">Henüz seçenek yok</span>}
        {options.map((o) => (
          <span key={o} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded border border-zinc-200 bg-white text-xs">
            {o}
            <button type="button" onClick={() => remove(o)} className="text-zinc-400 hover:text-red-600 p-0.5"><X size={11} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Seçenek yaz, Enter'a bas" className="h-8 text-sm" />
        <Button type="button" size="sm" variant="outline" onClick={add}><Plus size={13} /></Button>
      </div>
      {field.type === "select" && (
        <div className="mt-2">
          <Label className="text-xs text-zinc-500">Varsayılan değer</Label>
          <Select value={field.default || "__none"} onValueChange={(v) => onChange({ ...field, default: v === "__none" ? null : v })}>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Yok" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Yok</SelectItem>
              {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {field.type === "multiselect" && options.length > 0 && (
        <div className="mt-2">
          <Label className="text-xs text-zinc-500">Varsayılan seçili olanlar</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {options.map((o) => {
              const def = Array.isArray(field.default) ? field.default : [];
              const on = def.includes(o);
              return (
                <label key={o} className="inline-flex items-center gap-1.5 border border-zinc-200 rounded px-2 py-0.5 text-xs cursor-pointer">
                  <Checkbox checked={on} onCheckedChange={() => onChange({ ...field, default: on ? def.filter((x) => x !== o) : [...def, o] })} />
                  {o}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Saf özyinelemeli field kurucu (kendi adını JSX tag olarak KULLANMAZ; babel özyineleme sorununu önler).
function renderFieldBuilder({ field, onChange, onRemove, onMove, isFirst, isLast }) {
  const set = (patch) => onChange({ ...field, ...patch });
  const changeType = (t) => set({
    type: t,
    options: t === "select" || t === "multiselect" ? field.options || [] : [],
    default: t === "boolean" ? false : null,
    fields: t === "object" ? field.fields || [] : [],
  });
  const subs = field.fields || [];
  const setSubs = (fs) => set({ fields: fs });

  return (
    <div key={field.key} className="border border-zinc-200 rounded-md p-2.5 bg-white">
      <div className="flex items-center gap-2">
        <Input value={field.label} onChange={(e) => set({ label: e.target.value })} placeholder="Parametre adı (ör. Kasa Rengi)" className="h-8 text-sm flex-1" data-testid="field-label-input" />
        <Select value={field.type} onValueChange={changeType}>
          <SelectTrigger className="h-8 text-sm w-40" data-testid="field-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
        </Select>
        <button type="button" onClick={() => onMove(-1)} disabled={isFirst} className="p-1 rounded hover:bg-zinc-100 disabled:opacity-30"><ArrowUp size={14} /></button>
        <button type="button" onClick={() => onMove(1)} disabled={isLast} className="p-1 rounded hover:bg-zinc-100 disabled:opacity-30"><ArrowDown size={14} /></button>
        <button type="button" onClick={onRemove} className="p-1 rounded hover:bg-red-50 text-zinc-400 hover:text-red-600"><Trash2 size={14} /></button>
      </div>

      {(field.type === "select" || field.type === "multiselect") && <OptionsEditor field={field} onChange={onChange} />}

      {field.type === "boolean" && (
        <label className="flex items-center gap-2 mt-2 text-xs text-zinc-600 cursor-pointer">
          <Checkbox checked={!!field.default} onCheckedChange={(c) => set({ default: !!c })} />
          Varsayılan işaretli olsun
        </label>
      )}

      {field.type === "object" && (
        <div className="mt-2 pl-3 border-l-2 border-zinc-200">
          <div className="text-xs text-zinc-500 mb-1.5 flex items-center gap-1"><ChevronRight size={12} />Alt parametreler ({subs.length})</div>
          <div className="space-y-2">
            {subs.map((sf, i) => renderFieldBuilder({
              field: sf,
              onChange: (nf) => setSubs(arrUpdate(subs, i, nf)),
              onRemove: () => setSubs(arrRemove(subs, i)),
              onMove: (d) => setSubs(arrMove(subs, i, d)),
              isFirst: i === 0,
              isLast: i === subs.length - 1,
            }))}
            <Button type="button" variant="outline" size="sm" onClick={() => setSubs([...subs, newField()])}>
              <Plus size={13} className="mr-1" />Alt Parametre Ekle
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CategoryManager() {
  const [cats, setCats] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [name, setName] = useState("");
  const [fields, setFields] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [delTarget, setDelTarget] = useState(null);

  const active = useMemo(() => cats.find((c) => c.id === activeId), [cats, activeId]);

  const selectCat = (c) => { setActiveId(c.id); setName(c.name); setFields(c.fields || []); setDirty(false); };

  const load = async () => {
    const { data } = await api.get("/product-categories");
    setCats(data);
    return data;
  };

  useEffect(() => {
    (async () => {
      const data = await load();
      if (data.length) selectCat(data[0]);
    })();
    // eslint-disable-next-line
  }, []);

  const addCat = async () => {
    const n = newCatName.trim();
    if (!n) return;
    try {
      const { data } = await api.post("/product-categories", { name: n });
      setNewCatName("");
      await load();
      selectCat(data);
      toast.success("Kategori eklendi");
    } catch (e) { toast.error(e.response?.data?.detail || "Eklenemedi"); }
  };

  const saveCat = async () => {
    try {
      await api.put(`/product-categories/${activeId}`, { name: name.trim(), fields });
      setDirty(false);
      const data = await load();
      const cur = data.find((c) => c.id === activeId);
      if (cur) { setName(cur.name); setFields(cur.fields || []); }
      toast.success("Kaydedildi");
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const doDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/product-categories/${delTarget.id}`);
      const data = await load();
      if (delTarget.id === activeId) {
        if (data.length) selectCat(data[0]);
        else { setActiveId(null); setName(""); setFields([]); }
      }
      toast.success("Silindi");
    } catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
    setDelTarget(null);
  };

  const updateFields = (fs) => { setFields(fs); setDirty(true); };

  return (
    <Card className="p-5 border border-zinc-200 shadow-none" data-testid="category-manager">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-medium flex items-center gap-2"><Layers size={18} />Ürün Kategorileri & İmalat Detayları</h3>
          <div className="text-sm text-zinc-500">Her kategoriye özel imalat detay parametreleri tanımlayın. Proje ürünlerinde bu şemaya göre detay girilir; ustalar bu detayları görebilir.</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-5">
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} placeholder="Yeni kategori" className="h-9 text-sm" data-testid="new-category-input" />
            <Button onClick={addCat} variant="outline" size="sm" data-testid="add-category-btn"><Plus size={14} /></Button>
          </div>
          <div className="rounded-md border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
            {cats.length === 0 && <div className="p-3 text-sm text-zinc-500 text-center">Henüz kategori yok</div>}
            {cats.map((c) => (
              <div key={c.id} className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm ${c.id === activeId ? "bg-zinc-900 text-white" : "hover:bg-zinc-50"}`} onClick={() => selectCat(c)} data-testid={`category-row-${c.id}`}>
                <span className="truncate">{c.name}</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); setDelTarget(c); }} className={`p-0.5 rounded ${c.id === activeId ? "hover:bg-white/20" : "hover:bg-red-50 text-zinc-400 hover:text-red-600"}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>

        <div>
          {!active ? (
            <div className="text-sm text-zinc-500 py-8 text-center border border-dashed border-zinc-200 rounded-md">Düzenlemek için soldan bir kategori seçin veya yeni ekleyin.</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-zinc-500">Kategori Adı</Label>
                  <Input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} className="h-9" data-testid="category-name-input" />
                </div>
                {dirty && <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 self-end mb-1.5">Kaydedilmemiş</span>}
              </div>

              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">İmalat Detay Parametreleri</Label>
                <div className="space-y-2">
                  {fields.map((f, i) => renderFieldBuilder({
                    field: f,
                    onChange: (nf) => updateFields(arrUpdate(fields, i, nf)),
                    onRemove: () => updateFields(arrRemove(fields, i)),
                    onMove: (d) => updateFields(arrMove(fields, i, d)),
                    isFirst: i === 0,
                    isLast: i === fields.length - 1,
                  }))}
                  <Button type="button" variant="outline" size="sm" onClick={() => updateFields([...fields, newField()])} data-testid="add-field-btn">
                    <Plus size={13} className="mr-1" />Parametre Ekle
                  </Button>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={saveCat} disabled={!dirty} className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40" data-testid="save-category-btn">
                  <Save size={14} className="mr-1" />Kaydet
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kategori silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              "{delTarget?.name}" kategorisi silinecek. Bu kategoriye bağlı ürünlerin kategori bağlantısı kaldırılır (girilmiş detay verileri üründe kalır).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
